/**
 * Post-Test Store
 * Manages post-test modal state and queue communication
 * ✅ BUG FIX #4: Replaces fragile CustomEvents with reliable Pinia store
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import type { PendingPostTest } from "@/services/pendingPostTestService";
import { logDebug, logInfo, logSuccess, logError } from "@/utils/secureLogger";

export const usePostTestStore = defineStore("postTest", () => {
  // Modal state
  const showModal = ref(false);
  const currentTest = ref<PendingPostTest | null>(null);
  const isMandatory = ref(false);

  // Queue state
  const pendingQueue = ref<PendingPostTest[]>([]);
  const isProcessingQueue = ref(false);

  /**
   * Open post-test modal with session details
   */
  const openPostTest = (test: PendingPostTest) => {
    logInfo("Opening post-test modal via store", {
      sessionId: test.sessionId,
      subject: test.sessionSubject,
      isMandatory: test.isMandatory,
    });

    currentTest.value = test;
    isMandatory.value = test.isMandatory || false;
    showModal.value = true;
  };

  /**
   * Close post-test modal
   */
  const closePostTest = () => {
    logDebug("Closing post-test modal via store");
    showModal.value = false;
    currentTest.value = null;
    isMandatory.value = false;
  };

  /**
   * Set the pending test queue
   */
  const setQueue = (tests: PendingPostTest[]) => {
    logInfo("Setting post-test queue", {
      count: tests.length,
    });
    pendingQueue.value = tests;
  };

  /**
   * Load next test from queue
   */
  const loadNextFromQueue = async (): Promise<boolean> => {
    if (isProcessingQueue.value) {
      logDebug("Queue already being processed, skipping");
      return false;
    }

    if (pendingQueue.value.length === 0) {
      logDebug("Queue is empty, no more tests to load");
      return false;
    }

    isProcessingQueue.value = true;

    try {
      const nextTest = pendingQueue.value[0];
      logInfo("Loading next test from queue", {
        sessionId: nextTest.sessionId,
        subject: nextTest.sessionSubject,
        remainingInQueue: pendingQueue.value.length - 1,
      });

      // Remove from queue
      pendingQueue.value = pendingQueue.value.slice(1);

      // Open modal with next test
      openPostTest(nextTest);

      return true;
    } finally {
      isProcessingQueue.value = false;
    }
  };

  /**
   * Clear the queue
   */
  const clearQueue = () => {
    logDebug("Clearing post-test queue");
    pendingQueue.value = [];
    isProcessingQueue.value = false;
  };

  /**
   * Get remaining queue count
   */
  const queueCount = () => pendingQueue.value.length;

  /**
   * Refresh pending tests from Firestore
   * Used to immediately update banner after "Take Later" click
   * ✅ NOW INCLUDES: Automatic detection and re-creation of missing pending post-tests
   */
  const refreshQueue = async (userId: string) => {
    try {
      logInfo("Refreshing pending test queue from Firestore", {
        userId: userId.substring(0, 8) + "***",
      });

      // Import pendingPostTestService dynamically to avoid circular deps
      const { pendingPostTestService } = await import("@/services/pendingPostTestService");

      // ✅ STEP 1: Detect and re-create missing pending post-tests
      // This handles cases where post-test records were manually deleted
      logDebug("Detecting missing pending post-tests before queue refresh");
      const recreatedCount = await pendingPostTestService.detectAndRecreateMissingPendingPostTests(
        userId
      );

      if (recreatedCount > 0) {
        logSuccess("Re-created missing pending post-tests", {
          count: recreatedCount,
          userId: userId.substring(0, 8) + "***",
        });
      }

      // ✅ STEP 2: Load the updated queue from Firestore
      const tests = await pendingPostTestService.getAllPendingPostTests(userId);

      setQueue(tests);

      logSuccess("Pending test queue refreshed", {
        count: tests.length,
        recreated: recreatedCount,
        userId: userId.substring(0, 8) + "***",
      });

      return tests;
    } catch (error: any) {
      // Use console.error as fallback if logError is undefined (HMR issue)
      if (typeof logError === "function") {
        logError("Error refreshing pending test queue", error);
      } else {
        console.error("[PostTestStore] Error refreshing pending test queue:", error);
      }
      return [];
    }
  };

  return {
    // State
    showModal,
    currentTest,
    isMandatory,
    pendingQueue,
    isProcessingQueue,

    // Actions
    openPostTest,
    closePostTest,
    setQueue,
    loadNextFromQueue,
    clearQueue,
    queueCount,
    refreshQueue,
  };
});
